export const browserOriginateContractVersion = "browser_originate.v1" as const;
export const browserOriginateCapability =
  "browser_originated_sip_invite" as const;
export const browserOriginateIntentHeader = "X-Spark-VP-Intent" as const;

export const browserOriginateErrorCodes = [
  "invalid_request",
  "feature_unavailable",
  "runtime_not_ready",
  "intent_expired",
  "intent_consumed",
  "intent_rejected",
  "ownership_conflict",
  "call_cancelled",
  "dependency_unavailable",
  "idempotency_conflict",
  "internal_error",
] as const;

export type BrowserOriginateErrorCode =
  (typeof browserOriginateErrorCodes)[number];

export type BrowserOriginateErrorDetail = {
  reason: string;
  field?: string;
};

export type BrowserOriginatePreparedCall = {
  contractVersion: typeof browserOriginateContractVersion;
  capability: typeof browserOriginateCapability;
  preparedCallId: string;
  intent: string;
  expiresAt: string;
  sipTarget: string;
  intentHeader: typeof browserOriginateIntentHeader;
  correlationId: string;
  replayed: boolean;
};

export type BrowserOriginateDisplayFailure = {
  code: BrowserOriginateErrorCode | "unknown";
  message: string;
  requestId: string | null;
};

export type BrowserOriginateErrorEnvelope = {
  error: {
    code: BrowserOriginateErrorCode;
    message: string;
    requestId: string;
    details?: BrowserOriginateErrorDetail[];
  };
};

const displayMessages: Record<BrowserOriginateErrorCode, string> = {
  invalid_request:
    "This call request is invalid. Review the number and try again.",
  feature_unavailable:
    "Browser-originated calling is not available for your account yet.",
  runtime_not_ready: "Connecting calling service… Try again when it is ready.",
  intent_expired: "This call authorization expired. Start the call again.",
  intent_consumed:
    "This call authorization was already used. Refresh the call status before trying again.",
  intent_rejected: "The calling service rejected this call authorization.",
  ownership_conflict: "Calling is active in another Browser tab.",
  call_cancelled: "This prepared call was cancelled.",
  dependency_unavailable:
    "The calling service is temporarily unavailable. Try again shortly.",
  idempotency_conflict: "This call request conflicts with a previous attempt.",
  internal_error: "The calling service could not start this call. Try again.",
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function hasExactKeys(
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[] = [],
): boolean {
  const allowed = new Set([...required, ...optional]);
  return (
    required.every((key) => Object.hasOwn(value, key)) &&
    Object.keys(value).every((key) => allowed.has(key))
  );
}

function isBrowserOriginateErrorCode(
  value: unknown,
): value is BrowserOriginateErrorCode {
  return (
    typeof value === "string" &&
    (browserOriginateErrorCodes as readonly string[]).includes(value)
  );
}

function parseErrorDetails(
  value: unknown,
): BrowserOriginateErrorDetail[] | null {
  if (!Array.isArray(value)) return null;
  const details: BrowserOriginateErrorDetail[] = [];
  for (const candidate of value) {
    const detail = asRecord(candidate);
    if (
      !detail ||
      !hasExactKeys(detail, ["reason"], ["field"]) ||
      typeof detail.reason !== "string" ||
      detail.reason.length < 1 ||
      (detail.field !== undefined && typeof detail.field !== "string")
    ) {
      return null;
    }
    details.push({
      reason: detail.reason,
      ...(detail.field === undefined ? {} : { field: detail.field }),
    });
  }
  return details;
}

export function parseBrowserOriginateErrorEnvelope(
  value: unknown,
): BrowserOriginateErrorEnvelope | null {
  const outer = asRecord(value);
  const error = asRecord(outer?.error);
  if (
    !outer ||
    !error ||
    !hasExactKeys(outer, ["error"]) ||
    !hasExactKeys(error, ["code", "message", "requestId"], ["details"]) ||
    !isBrowserOriginateErrorCode(error.code) ||
    typeof error.message !== "string" ||
    error.message.length < 1 ||
    typeof error.requestId !== "string" ||
    error.requestId.length < 1
  ) {
    return null;
  }
  const details =
    error.details === undefined ? undefined : parseErrorDetails(error.details);
  if (details === null) return null;
  return {
    error: {
      code: error.code,
      message: error.message,
      requestId: error.requestId,
      ...(details === undefined ? {} : { details }),
    },
  };
}

/**
 * Render only Spark VP's recognized sanitized codes. Provider prose and error
 * details are retained for typed handling but never rendered to operators.
 */
export function toBrowserOriginateDisplayFailure(
  value: unknown,
): BrowserOriginateDisplayFailure {
  const envelope = parseBrowserOriginateErrorEnvelope(value);
  if (!envelope) {
    return {
      code: "unknown",
      message: "The calling service could not start this call. Try again.",
      requestId: null,
    };
  }
  return {
    code: envelope.error.code,
    message: displayMessages[envelope.error.code],
    requestId: envelope.error.requestId,
  };
}

export function parseBrowserOriginatePreparedCall(
  value: unknown,
): BrowserOriginatePreparedCall | null {
  const response = asRecord(value);
  if (
    !response ||
    !hasExactKeys(response, [
      "contractVersion",
      "capability",
      "preparedCallId",
      "intent",
      "expiresAt",
      "sipTarget",
      "intentHeader",
      "correlationId",
      "replayed",
    ]) ||
    response.contractVersion !== browserOriginateContractVersion ||
    response.capability !== browserOriginateCapability ||
    response.intentHeader !== browserOriginateIntentHeader ||
    typeof response.preparedCallId !== "string" ||
    response.preparedCallId.length < 1 ||
    typeof response.intent !== "string" ||
    !/^pcit1\.[A-Za-z0-9_-]{20,}$/.test(response.intent) ||
    typeof response.expiresAt !== "string" ||
    !/Z$/.test(response.expiresAt) ||
    Number.isNaN(Date.parse(response.expiresAt)) ||
    typeof response.sipTarget !== "string" ||
    !/^sip:[^@;?]+@[^;?]+$/.test(response.sipTarget) ||
    typeof response.correlationId !== "string" ||
    !/^pcorr1\.[A-Za-z0-9_-]{12,}$/.test(response.correlationId) ||
    typeof response.replayed !== "boolean"
  ) {
    return null;
  }
  return {
    contractVersion: browserOriginateContractVersion,
    capability: browserOriginateCapability,
    preparedCallId: response.preparedCallId,
    intent: response.intent,
    expiresAt: response.expiresAt,
    sipTarget: response.sipTarget,
    intentHeader: browserOriginateIntentHeader,
    correlationId: response.correlationId,
    replayed: response.replayed,
  };
}
